import { createContext, useCallback, useContext, useRef, useState } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { AlertTriangle, HelpCircle } from 'lucide-react'
import { cn } from '@/utils/cn'

export interface ConfirmOptions {
  title: string
  description?: string
  confirmLabel?: string
  cancelLabel?: string
  variant?: 'default' | 'danger'
}

type ConfirmFn = (options: ConfirmOptions) => Promise<boolean>

const ConfirmContext = createContext<ConfirmFn | null>(null)

// Native window.confirm() can't be styled or branded and treats every action
// (deactivating an org, deleting a label) with the same plain "OK" button.
// This provider renders one app-styled dialog and resolves a promise so call
// sites can keep the same `if (await confirm(...)) doThing()` shape.
export function ConfirmDialogProvider({ children }: { children: React.ReactNode }) {
  const [options, setOptions] = useState<ConfirmOptions | null>(null)
  const resolveRef = useRef<((value: boolean) => void) | null>(null)

  const confirm = useCallback<ConfirmFn>((opts) => {
    setOptions(opts)
    return new Promise<boolean>(resolve => {
      resolveRef.current = resolve
    })
  }, [])

  const settle = (value: boolean) => {
    resolveRef.current?.(value)
    resolveRef.current = null
    setOptions(null)
  }

  const danger = options?.variant === 'danger'

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <AnimatePresence>
        {options && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 p-4" role="alertdialog" aria-modal="true" aria-labelledby="confirm-dialog-title">
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.96 }}
              className="w-full max-w-sm rounded-2xl border border-border bg-bg-elevated p-6 shadow-2xl"
            >
              <div className={cn('mb-4 flex h-10 w-10 items-center justify-center rounded-xl', danger ? 'bg-status-error/10' : 'bg-brand-500/10')}>
                {danger
                  ? <AlertTriangle className="h-5 w-5 text-status-error" />
                  : <HelpCircle className="h-5 w-5 text-brand-500" />}
              </div>
              <h3 id="confirm-dialog-title" className="mb-1 text-sm font-semibold text-text-primary">{options.title}</h3>
              {options.description && <p className="mb-5 text-xs text-text-secondary">{options.description}</p>}
              <div className={cn('flex gap-2', !options.description && 'mt-5')}>
                <button
                  onClick={() => settle(false)}
                  className="flex-1 rounded-xl border border-border px-3 py-2 text-sm text-text-secondary hover:bg-bg-muted"
                >
                  {options.cancelLabel ?? 'Cancel'}
                </button>
                <button
                  onClick={() => settle(true)}
                  className={cn(
                    'flex-1 rounded-xl px-3 py-2 text-sm font-medium text-white',
                    danger ? 'bg-status-error hover:opacity-90' : 'bg-brand-500 hover:bg-brand-600',
                  )}
                >
                  {options.confirmLabel ?? 'Confirm'}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </ConfirmContext.Provider>
  )
}

// Returns a function that opens the shared confirm dialog and resolves to
// whether the user confirmed — a drop-in async replacement for window.confirm().
export function useConfirm(): ConfirmFn {
  const confirm = useContext(ConfirmContext)
  if (!confirm) throw new Error('useConfirm() must be used within a ConfirmDialogProvider')
  return confirm
}
